








































































































import AdminBrandId from "@/mixins/adminBrandId";
import Context from "@/mixins/context";
import Datetime from "@/components/base/Datetime.vue";
import OrderItemsSummary from "@/components/app/global/orders/orderItemsSummary.vue";
import StatsStorageMixin from "@/mixins/statsStorageMixin";
import UResizableTable from "@/components/app-base/UResizableTable.vue";
import i18n from "@/i18n";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { DataModules } from "@/store/modules/data";
import { DynamicColumns } from "@/mixins/dynamicColumns";
import { Sorters } from "@/data/table";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { IDynamicColumn } from "@/models/dynamicColumns";
import { ITableConfig, IFilter, ISorter } from "@/models/tables";
import { ReferralType } from "@/data/enums/trackedReferrals";
import { UUID } from "@/data/enums";
import { mapGetters } from "vuex";
import { trackedReferralsFilters } from "@/data/filters/trackedReferrals";
import { trackedReferralsSorters } from "@/data/sorters/trackedReferrals";

@Component({
  computed: {
    ...mapGetters({
      isMultibrand: "brand/isMultibrand"
    })
  },
  components: {
    UResizableTable,
    "stat-header": () => import("@/components/app/global/stats/statHeader.vue")
  }
})
export default class TrackedReferralsTable extends Mixins(
  FilteringSortingTableStore,
  DynamicColumns,
  Context,
  StatsStorageMixin,
  AdminBrandId
) {
  // Props
  @Prop({ required: true, default: ReferralType.SIGNUP })
  public readonly type!: ReferralType;

  public defaults: ITableConfig = {
    page: 1,
    limit: 50,
    sort: {
      field: "created_at",
      direction: Sorters.DESCENDING
    },
    filters: []
  };

  public isMultibrand!: boolean;

  public defaultColumnWidth = "120px";
  public filters: IFilter[] = trackedReferralsFilters(this.type);

  public availableColumns: IDynamicColumn[] = [
    {
      id: "client.fullname",
      label: i18n.t("_.client"),
      component: "u-client-link",
      props: (row: any) => ({
        clientId: this.$_.get(row, "client_id"),
        client: this.$_.get(row, "client"),
        class: "has-text-dark"
      }),
      thAttr: {
        class: "is-compact",
        style: { width: "200px" }
      }
    },
    {
      id: "client.email",
      field: "client.email",
      label: i18n.t("_.client_email")
    },
    {
      id: "medium",
      field: "medium",
      label: i18n.t("_.medium")
    },
    {
      id: "source",
      field: "source",
      label: i18n.t("_.source")
    },
    {
      id: "campaign",
      field: "campaign",
      label: i18n.t("_.campaign")
    },
    {
      id: "content",
      field: "content",
      label: i18n.t("_.content")
    },
    {
      id: "term",
      field: "term",
      label: i18n.t("_.term")
    },
    {
      id: "created_at",
      label: i18n.t("_.created_at"),
      component: Datetime,
      props: (row: any) => ({
        key: row.id,
        datetime: row.created_at
      })
    }
  ];

  public sorters: ISorter[] = trackedReferralsSorters();

  public defaultColumns: string[] = [
    "client.fullname",
    "client.email",
    "medium",
    "source",
    "campaign",
    "content",
    "term",
    "created_at"
  ];

  public dataModule: string = DataModules.UPM_TACKING;

  public async created() {
    this.apiProps = { with: "brand,client,account" };
    this.setupMultibrand();
    this.setupOrderTable();
    this.init();
    await this.getData();
  }

  public setupMultibrand() {
    if (this.brandId === UUID.ORG) return;

    this.availableColumns = this.$_.union(this.availableColumns, [
      {
        id: "brand.id",
        field: "brand.id",
        label: i18n.t("_.brand"),
        function: row => row.brand.name
      }
    ]);

    const index = this.defaultColumns.indexOf("client.email");
    this.defaultColumns.splice(index + 1, 0, "brand.id");
  }

  public setupOrderTable() {
    if (this.type !== ReferralType.ORDER) return;

    this.apiProps = {
      with: [
        "account",
        "brand",
        "client",
        "invoice",
        "invoice.products",
        "invoice.status"
      ].join()
    };

    this.availableColumns = this.$_.union(this.availableColumns, [
      {
        id: "invoice.id",
        label: this.$t("_.order_id", { orderId: "" }).toString(),
        component: "u-order-link",
        field: "invoice.number",
        props: (row: any) => {
          return {
            orderId: this.$_.get(row, "invoice_id"),
            clientId: this.$_.get(row, "client.id"),
            tag: "u-link"
          };
        }
      },
      {
        id: "invoice.status",
        label: this.$t("_.order_status").toString(),
        component: "status-label",
        props: (row: any) => ({
          status: this.$_.get(row, "invoice.status.code"),
          type: "order"
        })
      },
      {
        id: "invoice.products",
        label: i18n.t("_.order_items"),
        component: OrderItemsSummary,
        props: (row: any) => ({
          items: this.$_.get(row, "invoice.products")
        })
      },
      {
        id: "invoice.total",
        label: i18n.t("_.total"),
        function: row => this.$_.get(row, "invoice.total_amount_formatted")
      }
    ]);

    let index = this.defaultColumns.indexOf("client.fullname");
    this.defaultColumns.splice(index, 0, "invoice.status"); // Insert before client columns
    this.defaultColumns.splice(++index, 0, "invoice.id"); // Insert before client columns
    this.defaultColumns.splice(++index, 0, "invoice.products");
    this.defaultColumns.splice(++index, 0, "invoice.total");

    this.sorters = this.$_.union(this.sorters, [
      {
        field: "invoice_id",
        label: this.$t("_.order_id", { orderId: "" }).toString()
      }
    ]);
  }

  // overrides mixin method
  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(params, {
      params: {
        ...params.filter,
        ...this.ordersFilter,
        ...this.typeFilter
      }
    });
  }

  public get ordersFilter() {
    // Omit unconverted baskets
    return (
      this.type === ReferralType.ORDER && {
        "filter[invoice.id|gt]": "0",
        "filter[invoice.status.code|neq]": "invoice_draft"
      }
    );
  }

  public get typeFilter() {
    return this.type ? { "filter[type]": this.type } : {};
  }
}
